import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Handshake, MapPin, Wallet, StickyNote } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { MatchScore, OwnerBadge, Reasons, Fade, priceLabel } from "@/components/proptech";
import { SmartMatchDialog } from "@/components/SmartMatchDialog";
import { api, apiError, peso, track } from "@/lib/api";
import { PropertyCover } from "@/components/AdaptiveMediaFrame";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { propertyTypeLabel } from "@/lib/taxonomy";

const CLASS_STYLE = { STRONG: "bg-emerald-100 text-emerald-700", POSSIBLE: "bg-amber-100 text-amber-700" };
const classLabel = (c) => (c === "STRONG" ? "Strong Match" : c === "POSSIBLE" ? "Possible Match" : "");
const ClassBadge = ({ c }) => (classLabel(c) ? <span data-testid="class-badge" className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CLASS_STYLE[c]}`}>{classLabel(c)}</span> : null);

export default function PropertyMatches() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get(`/matches/for-property/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  useEffect(() => { track("match_view", { property_id: id }); }, [id]);

  const collaborate = async (buyer) => {
    setBusy(buyer.id);
    try {
      const { data: c } = await api.post("/collaborations", {
        property_id: id, buyer_requirement_id: buyer.id,
        note: `Hi! My listing "${data.property.title}" looks like a strong fit for your buyer (${buyer.buyer_alias}). Want to co-broke?`,
      });
      toast.success("Collaboration room opened!");
      nav(`/collaboration/${c.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  if (!data) return <PageShell testid="property-matches-page"><p className="text-slate-500">Loading matches…</p></PageShell>;
  const p = data.property;

  return (
    <PageShell testid="property-matches-page">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
      <Fade>
        <div className="bg-primary text-primary-foreground rounded-2xl overflow-hidden mb-8 md:flex md:items-stretch">
          <div className="md:w-72 h-48 md:h-56 shrink-0"><PropertyCover listing={p} aspect={null} className="h-full w-full" /></div>
          <div className="p-6 sm:p-7 flex-1 flex flex-col justify-center">
            <p className="text-[11px] uppercase tracking-wider text-accent font-semibold mb-1">Your Listing</p>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold">{p.title}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-slate-300 text-sm">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{[p.neighborhood, p.city].filter(Boolean).join(", ")}</span>
              <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4" />{p.listing_type === "Unit Hosting" ? `₱${(p.nightly_rate || 0).toLocaleString()} / night` : priceLabel(p)}</span>
              <span>{propertyTypeLabel(p.property_type)} · {p.listing_type} · {p.bedrooms} BR</span>
            </div>
          </div>
        </div>
      </Fade>
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">{data.results.length} matching {data.results.length === 1 ? "buyer" : "buyers"}{(data.strong_count || data.possible_count) ? <span className="text-slate-400 font-normal"> · {data.strong_count} Strong · {data.possible_count} Possible</span> : null}</h2>
      <div className="space-y-4" data-testid="match-results">
        {data.results.length === 0 && <p className="text-slate-500">No strong buyer matches yet.</p>}
        {data.results.map((b, i) => (
          <Fade key={b.id} delay={0.05 * i}>
            <div data-testid={`match-card-${b.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-col sm:flex-row gap-4 hover:shadow-md transition-shadow">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1"><span className="text-[11px] font-semibold uppercase tracking-wide bg-secondary text-slate-600 px-2 py-0.5 rounded">{b.listing_type}</span><span className="text-[11px] text-slate-400">{propertyTypeLabel(b.property_type)}</span></div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{b.buyer_alias}</h3>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-sm text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{b.cities.join(", ")}</span>
                  <span className="flex items-center gap-1"><Wallet className="h-4 w-4" />{peso(b.budget_min)}–{peso(b.budget_max)}</span>
                  <span>{b.min_bedrooms}+ BR</span>
                </div>
                {b.notes && <p className="text-sm text-slate-500 mt-2 flex items-start gap-1.5"><StickyNote className="h-4 w-4 mt-0.5 shrink-0" />{b.notes}</p>}
                <div className="mt-3"><Reasons match={b.match} /></div>
                <div className="mt-3 pt-3 border-t border-border"><OwnerBadge owner={b.owner} small /></div>
              </div>
              <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:justify-between shrink-0">
                <div className="flex flex-col items-center sm:items-end gap-1.5">
                  <MatchScore score={b.match.score} />
                  <ClassBadge c={b.classification} />
                </div>
                {b.is_own ? (
                  <div className="flex flex-col items-end gap-1.5">
                    <span data-testid={`own-requirement-${b.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Your Buyer Need</span>
                    <Button data-testid={`view-requirement-${b.id}`} onClick={() => nav(`/buyer/${b.id}/matches`)} variant="outline" className="rounded-xl font-semibold whitespace-nowrap">View Buyer Need</Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-end gap-2">
                    <SmartMatchDialog testid={`why-${b.id}`} buyerId={b.id} propertyId={id} onCollaborate={() => collaborate(b)} busy={busy === b.id} />
                    <Button data-testid={`collaborate-${b.id}`} onClick={() => collaborate(b)} disabled={busy === b.id} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl font-semibold active:scale-95 transition-transform whitespace-nowrap shrink-0"><Handshake className="h-4 w-4 mr-1.5" /> {busy === b.id ? "Opening…" : "Collaborate"}</Button>
                  </div>
                )}
              </div>
            </div>
          </Fade>
        ))}
      </div>
    </PageShell>
  );
}
